import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getSeoSnapshot } from "@/lib/seo-monitor.functions";
import { contentDates } from "@/content/content-dates";
import { SITE_URL } from "@/content/seo";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

/** Strony przygotowane pod cytowania AI (Article + speakable + FAQ). */
const AI_READY = new Set([
  "/psychologia-biznesu",
  "/syndrom-oszusta",
  "/perfekcjonizm",
  "/stres-w-pracy-a-decyzje",
  "/samotnosc-lidera",
]);

const QUESTION_WORDS = /^(jak|czy|co|dlaczego|kiedy|gdzie|ile|kto|czym|po co|jakie|jaki|jaka)\b/i;

export const Route = createFileRoute("/panel-seo")({
  head: () => ({
    meta: [
      { title: "Panel monitoringu SEO | Sylwia Sitkowska" },
      { name: "description", content: "Wewnętrzny panel monitoringu widoczności w Google Search Console i gotowości treści pod AI search." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PanelSeo,
});

function pct(value: number) {
  return `${(value * 100).toFixed(1)}%`;
}

function delta(current: number, previous: number) {
  if (!previous) return null;
  return (current - previous) / previous;
}

function Kpi({ label, value, change, invert }: { label: string; value: string; change: number | null; invert?: boolean }) {
  const positive = change === null ? null : invert ? change < 0 : change > 0;
  return (
    <div className="rounded-lg border border-ink/10 bg-surface p-5">
      <p className="text-xs uppercase tracking-[0.18em] text-ink/60">{label}</p>
      <p className="mt-2 font-display text-3xl text-ink">{value}</p>
      {change !== null && (
        <p className={`mt-1 text-sm ${positive ? "text-coral-text" : "text-ink/60"}`}>
          {change > 0 ? "+" : ""}
          {(change * 100).toFixed(1)}% vs poprzednie 28 dni
        </p>
      )}
    </div>
  );
}

function PanelSeo() {
  const fetchSnapshot = useServerFn(getSeoSnapshot);
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["seo-snapshot"],
    queryFn: () => fetchSnapshot(),
    staleTime: 1000 * 60 * 60,
  });

  const snapshot = data?.ok ? data.data : null;
  const selection = snapshot && "status" in snapshot ? snapshot : null;
  const stats = snapshot && !("status" in snapshot) ? snapshot : null;

  return (
    <main className="mx-auto w-full max-w-5xl px-6 py-16">
      <header className="mb-10">
        <p className="text-xs uppercase tracking-[0.24em] text-ink/50">Monitoring</p>
        <h1 className="mt-2 font-display text-4xl text-ink">Panel SEO i AI search</h1>
        <p className="mt-3 max-w-2xl text-ink/70">
          Dane z Google Search Console za ostatnie 28 dni, porównane z poprzednim okresem. Search Console
          raportuje z około dwudniowym opóźnieniem, więc najświeższe dni mogą być jeszcze niepełne.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-full border border-ink/20 px-4 py-2 text-sm text-ink transition hover:border-ink/40"
          >
            {isFetching ? "Odświeżam…" : "Odśwież dane"}
          </button>
          <a
            href="/panel-seo-asystent"
            className="rounded-full border border-ink/20 px-4 py-2 text-sm text-ink transition hover:border-ink/40"
          >
            Asystent SEO
          </a>
        </div>
      </header>

      {isLoading && <p className="text-ink/60">Pobieram dane z Search Console…</p>}
      {(isError || (data && !data.ok)) && (
        <p className="rounded-lg border border-ink/10 bg-surface p-5 text-ink/70">
          {data && !data.ok ? data.message : "Nie udało się pobrać danych z Search Console."}
        </p>
      )}
      {selection && (
        <p className="rounded-lg border border-ink/10 bg-surface p-5 text-ink/70">
          W Search Console znaleziono kilka usług obejmujących tę domenę: {selection.candidates.join(", ")}.
          Wybierz jedną, a podepnę ją na stałe.
        </p>
      )}

      {stats && (
        <div className="space-y-12">
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Kliknięcia" value={String(stats.totals.clicks)} change={delta(stats.totals.clicks, stats.previousTotals.clicks)} />
            <Kpi label="Wyświetlenia" value={String(stats.totals.impressions)} change={delta(stats.totals.impressions, stats.previousTotals.impressions)} />
            <Kpi label="CTR" value={pct(stats.totals.ctr)} change={delta(stats.totals.ctr, stats.previousTotals.ctr)} />
            <Kpi label="Średnia pozycja" value={stats.totals.position.toFixed(1)} change={delta(stats.totals.position, stats.previousTotals.position)} invert />
          </section>

          <section>
            <h2 className="font-display text-2xl text-ink">Widoczność dzień po dniu</h2>
            <div className="mt-4 h-72 w-full rounded-lg border border-ink/10 bg-surface p-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.days.map((d) => ({ date: d.keys[0], clicks: d.clicks, impressions: d.impressions }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-ink/10" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="impressions" stroke="#7B2458" dot={false} name="Wyświetlenia" />
                  <Line type="monotone" dataKey="clicks" stroke="#111111" dot={false} name="Kliknięcia" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section>
            <h2 className="font-display text-2xl text-ink">Zapytania pytające (sygnał AI search)</h2>
            <p className="mt-2 text-sm text-ink/60">
              Search Console nie pokazuje wprost, które fragmenty cytują modele AI. Najbliższym dostępnym
              sygnałem są zapytania w formie pytań, bo to na nie odpowiadają zarówno fragmenty w Google, jak
              i wyszukiwarki generatywne.
            </p>
            <QueryTable rows={stats.queries.filter((r) => QUESTION_WORDS.test(r.keys[0] ?? ""))} empty="Brak zapytań pytających w tym okresie." />
          </section>

          <section>
            <h2 className="font-display text-2xl text-ink">Wszystkie top zapytania</h2>
            <QueryTable rows={stats.queries.slice(0, 20)} empty="Brak danych o zapytaniach." />
          </section>

          <section>
            <h2 className="font-display text-2xl text-ink">Strony: wyniki i gotowość pod AI</h2>
            <div className="mt-4 overflow-x-auto rounded-lg border border-ink/10 bg-surface">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-ink/10 text-xs uppercase tracking-[0.14em] text-ink/60">
                  <tr>
                    <th className="p-3">Strona</th>
                    <th className="p-3">Kliknięcia</th>
                    <th className="p-3">Wyświetlenia</th>
                    <th className="p-3">Pozycja</th>
                    <th className="p-3">Ostatnia zmiana</th>
                    <th className="p-3">AI-ready</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.pages.map((row) => {
                    const path = (row.keys[0] ?? "").replace(SITE_URL, "") || "/";
                    return (
                      <tr key={path} className="border-b border-ink/5 last:border-0">
                        <td className="p-3">{path}</td>
                        <td className="p-3">{row.clicks}</td>
                        <td className="p-3">{row.impressions}</td>
                        <td className="p-3">{row.position.toFixed(1)}</td>
                        <td className="p-3">{contentDates[path]?.dateModified ?? "-"}</td>
                        <td className="p-3">{AI_READY.has(path) ? "tak" : "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <p className="text-xs text-ink/50">
            Usługa: {stats.siteUrl} · zakres {stats.range.start}, {stats.range.end} · odświeżono{" "}
            {new Date(stats.refreshedAt).toLocaleString("pl-PL")}
          </p>
        </div>
      )}
    </main>
  );
}

function QueryTable({ rows, empty }: { rows: { keys: string[]; clicks: number; impressions: number; position: number }[]; empty: string }) {
  if (rows.length === 0) return <p className="mt-4 text-ink/60">{empty}</p>;
  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-ink/10 bg-surface">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-ink/10 text-xs uppercase tracking-[0.14em] text-ink/60">
          <tr>
            <th className="p-3">Zapytanie</th>
            <th className="p-3">Kliknięcia</th>
            <th className="p-3">Wyświetlenia</th>
            <th className="p-3">Pozycja</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.keys[0]} className="border-b border-ink/5 last:border-0">
              <td className="p-3">{row.keys[0]}</td>
              <td className="p-3">{row.clicks}</td>
              <td className="p-3">{row.impressions}</td>
              <td className="p-3">{row.position.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
